import { collection, doc, getDocs, query, where, limit, setDoc, addDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from "firebase/auth";
import { db, auth, secondaryAuth } from "./firebase";

// ════════════════════════════════════════════════════════════════
// Super-Admin Actions
//
// Mutations available only to super-admins. The Firestore rules enforce
// that only users with role: "super_admin" can call these (so even if
// someone bypasses the UI, they can't write).
//
// All actions in this file run multiple sequential writes. They're not
// atomic transactions because Firestore transactions can't span subcollections
// of different documents and don't work with Auth user creation. If something
// fails partway, the result is a partially-created school. The order is
// designed so that the most fragile step (Auth user creation) happens first;
// if the Firestore writes fail later, you can retry.
// ════════════════════════════════════════════════════════════════

// Slug validation: lowercase letters, numbers, hyphens, underscores, 2-40 chars
const SLUG_REGEX = /^[a-z0-9_-]{2,40}$/;

export function isValidSlug(slug) {
  return typeof slug === "string" && SLUG_REGEX.test(slug);
}

// Generate a slug from a school name. Examples:
//   "Colegio San Pedro"  -> "colegio-san-pedro"
//   "Escuela María Auxiliadora" -> "escuela-maria-auxiliadora"
export function suggestSlug(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip accents
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

// Generate a random password: 10 chars, alphanumeric + a couple of symbols
export function generatePassword() {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // no I, O for clarity
  const lower = "abcdefghijkmnpqrstuvwxyz"; // no l, o
  const digits = "23456789"; // no 0, 1
  const symbols = "@#$%&*";
  const all = upper + lower + digits + symbols;
  // Ensure at least one of each category
  let pwd = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];
  for (let i = pwd.length; i < 12; i++) {
    pwd.push(all[Math.floor(Math.random() * all.length)]);
  }
  // Shuffle
  pwd = pwd.sort(() => Math.random() - 0.5);
  return pwd.join("");
}

// Check if a slug is already in use. Returns true if available.
export async function isSlugAvailable(slug) {
  if (!isValidSlug(slug)) return false;
  const q = query(collection(db, "schools"), where("slug", "==", slug), limit(1));
  const snap = await getDocs(q);
  return snap.empty;
}

// ────────────────────────────────────────────────────────────────
// registerNewSchool
//
// Creates a new school + initial admin in 6 sequential writes.
// Uses secondaryAuth so the super-admin's session is NOT replaced
// when we create the new admin user.
//
// schoolData = { name, slug, contactEmail, plan }
// adminData  = { name, email, password }
//
// Returns { schoolId, slug, adminUid }
// ────────────────────────────────────────────────────────────────
export async function registerNewSchool(schoolData, adminData) {
  // ─── Validations ───
  if (!schoolData?.name?.trim()) throw new Error("Falta el nombre de la escuela");
  if (!isValidSlug(schoolData.slug)) {
    throw new Error("Código de escuela inválido. Solo letras minúsculas, números, guiones (2-40 caracteres).");
  }
  if (!schoolData.contactEmail?.trim()) throw new Error("Falta el email de contacto");
  if (!adminData?.name?.trim()) throw new Error("Falta el nombre del administrador");
  if (!adminData?.email?.trim()) throw new Error("Falta el email del administrador");
  if (!adminData?.password || adminData.password.length < 6) {
    throw new Error("La contraseña del admin debe tener al menos 6 caracteres");
  }

  const slug = schoolData.slug.trim().toLowerCase();
  const plan = schoolData.plan || "free";

  // ─── Step 1: re-check slug availability (defense against race) ───
  const available = await isSlugAvailable(slug);
  if (!available) throw new Error(`El código "${slug}" ya está en uso por otra escuela`);

  // ─── Step 2: create Auth user for the admin (using secondaryAuth) ───
  let adminUid;
  try {
    const cred = await createUserWithEmailAndPassword(secondaryAuth, adminData.email.trim(), adminData.password);
    adminUid = cred.user.uid;
  } catch (e) {
    if (e.code === "auth/email-already-in-use") {
      throw new Error(`El email ${adminData.email} ya está en uso por otro usuario`);
    }
    if (e.code === "auth/invalid-email") {
      throw new Error("El email del admin tiene un formato inválido");
    }
    throw new Error(`Error creando admin: ${e.message}`);
  }

  // From here on, if anything fails, we have a "dangling" Auth user.
  // We log a warning so the super-admin can clean it up manually.
  const cleanupNote = `IMPORTANTE: El usuario de Firebase Auth (${adminData.email}, uid: ${adminUid}) fue creado pero la escuela falló parcialmente. Borralo manualmente desde Firebase Console → Authentication antes de reintentar.`;

  try {
    const nowIso = new Date().toISOString();

    // ─── Step 3: create school document ───
    const schoolRef = await addDoc(collection(db, "schools"), {
      name: schoolData.name.trim(),
      slug,
      active: true,
      plan,
      contactEmail: schoolData.contactEmail.trim(),
      createdAt: nowIso,
      config: {
        currency: "CRC",
        locale: "es-CR",
        logoUrl: null,
      },
    });
    const schoolId = schoolRef.id;

    // ─── Step 4: create userRoles entry for the admin ───
    await setDoc(doc(db, "userRoles", adminUid), {
      role: "admin",
      schoolId,
      email: adminData.email.trim(),
      createdAt: nowIso,
    });

    // ─── Step 5: create vendor profile for the admin ───
    await setDoc(doc(db, "schools", schoolId, "vendors", adminUid), {
      name: adminData.name.trim(),
      email: adminData.email.trim(),
      active: true,
      isDefault: false,
      isAdmin: true,
      locations: ["loc_general"],
      createdAt: nowIso,
    });

    // ─── Step 6: create default location ───
    await setDoc(doc(db, "schools", schoolId, "locations", "loc_general"), {
      name: "General",
      active: true,
      isDefault: true,
    });

    // ─── Step 7: sign out the secondary auth (don't keep new admin session around) ───
    try { await signOut(secondaryAuth); } catch {}

    return { schoolId, slug, adminUid };
  } catch (e) {
    // Try to sign out secondary auth even on failure
    try { await signOut(secondaryAuth); } catch {}
    console.error(cleanupNote);
    throw new Error(`${e.message} — ${cleanupNote}`);
  }
}

// ────────────────────────────────────────────────────────────────
// updateSchool — edit school metadata (name, plan, contactEmail, config)
// ────────────────────────────────────────────────────────────────
export async function updateSchool(schoolId, data) {
  if (!schoolId) throw new Error("schoolId requerido");
  const allowed = ["name", "plan", "contactEmail", "config"];
  const clean = {};
  for (const k of allowed) {
    if (data[k] !== undefined) clean[k] = data[k];
  }
  if (Object.keys(clean).length === 0) throw new Error("Nada que actualizar");
  await updateDoc(doc(db, "schools", schoolId), clean);
}

// ────────────────────────────────────────────────────────────────
// toggleSchoolActive — suspend or reactivate a school
// When active=false, users of that school can't log in (SchoolContext
// detects this and rejects). Data is preserved.
// ────────────────────────────────────────────────────────────────
export async function toggleSchoolActive(schoolId, active) {
  if (!schoolId) throw new Error("schoolId requerido");
  await updateDoc(doc(db, "schools", schoolId), {
    active: !!active,
    ...(active ? { reactivatedAt: new Date().toISOString() } : { suspendedAt: new Date().toISOString() }),
  });
}

// ────────────────────────────────────────────────────────────────
// softDeleteSchool — mark a school as logically deleted
// Sets active=false + deleted=true + deletedAt timestamp.
// Data is preserved for audit/recovery. The school disappears from
// the main list unless the super-admin explicitly filters "Eliminadas".
// ────────────────────────────────────────────────────────────────
export async function softDeleteSchool(schoolId) {
  if (!schoolId) throw new Error("schoolId requerido");
  await updateDoc(doc(db, "schools", schoolId), {
    active: false,
    deleted: true,
    deletedAt: new Date().toISOString(),
  });
}

// ────────────────────────────────────────────────────────────────
// restoreSchool — undo a soft delete
// ────────────────────────────────────────────────────────────────
export async function restoreSchool(schoolId) {
  if (!schoolId) throw new Error("schoolId requerido");
  await updateDoc(doc(db, "schools", schoolId), {
    active: true,
    deleted: false,
    restoredAt: new Date().toISOString(),
  });
}

// ────────────────────────────────────────────────────────────────
// sendPasswordResetForAdmin — send password reset email via Firebase Auth
// Uses the primary `auth` instance (not secondaryAuth) because the reset
// email is tied to the Auth project config. This doesn't affect sessions.
// ────────────────────────────────────────────────────────────────
export async function sendPasswordResetForAdmin(email) {
  if (!email) throw new Error("Email requerido");
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch (e) {
    if (e.code === "auth/user-not-found") {
      throw new Error(`No existe un usuario con el email ${email}`);
    }
    if (e.code === "auth/invalid-email") {
      throw new Error("Email con formato inválido");
    }
    throw new Error(`Error enviando reset: ${e.message}`);
  }
}
